import { createClient } from '@supabase/supabase-js';

/**
 * What kind of link brought the user here, captured before anything consumes it.
 *
 * Supabase delivers invite and password-reset tokens in the URL fragment, and
 * `detectSessionInUrl` strips the fragment as soon as the client is created.
 * Reading it at module load — above createClient, deliberately — is the only
 * point at which `type` is still visible.
 *
 * Without this an invited user is signed in but has no password, so the next
 * visit strands them at a sign-in form they cannot satisfy.
 */
function captureLinkType() {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  const params = new URLSearchParams(hash.slice(1));
  const type = params.get('type');

  // An expired or already-used link arrives as an error instead of a session.
  const error = params.get('error_description') || params.get('error');

  return type || error ? { type, error } : null;
}

export const authLink = captureLinkType();

const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY must be set. See apps/web/.env.example.'
  );
}

/**
 * Supabase is used for authentication only.
 *
 * All data goes through our own API, which holds the service-role key and is
 * the single writer — that is what keeps the audit log and the mailing-totals
 * triggers from being sidestepped. The anon key here grants nothing on its own:
 * every table denies writes to `authenticated` by policy.
 */
export const supabase = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: true,
  },
});

/** Current access token, or null when signed out. */
export async function accessToken() {
  const { data } = await supabase.auth.getSession();
  return data.session?.access_token ?? null;
}
